/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { CardItem } from '../../../../Card/CardItem';
import { contentPackageLeadStyle } from '../../style/contentPackageLeadStyle';
import { contentPackageMediumStyle } from '../../style/contentPackageMediumStyle';
import { contentPackageSmallStyle } from '../../style/contentPackageSmallStyle';
import { getContentPackageStackCardDatum } from '../utils/contentPackageUtils';
import { GridColumn, GridRow } from '../../../../responsive/atoms/Grid';
import { ContentPackageHeaderSection } from '../../ContentPackageHeaderSection';
import { Container } from '../../../../responsive/atoms/Container';
import { Ad } from '../../../../responsive/atoms/Ad';
import { theme } from '../../../../../domain/theme';
import { useBreakpoint } from '../../../../../hooks/useBreakpoint';
import { nullValidator } from '../../../../propTypes/validators';

let currentBreakpoint;

const styles = {
  leadContainer: css`
    display: flex;
    flex-wrap: wrap;
  `,
  leadContainerHeader: css`
    flex: 0 0 100%;
    order: 1;
    ${theme.mq.tablet} {
      order: 2;
    }
  `,
  leadContainerCard: css`
    flex: 0 0 100%;
    order: 2;
    ${theme.mq.tablet} {
      order: 1;
    }
  `
};

export const ContentPackageGridTemplate = ({
  gridConfig,
  data,
  content,
  isAboveTheFold
}) => {
  const [breakpoint] = useBreakpoint();
  // When breakpoint changes, reset window.___ng_animatedImagesAlreadyRendered,
  // which is used by CardItem to avoid showing multiple animated images from
  // different paragraphs at the same time.
  if (currentBreakpoint && currentBreakpoint.name !== breakpoint.name) {
    window.___ng_animatedImagesAlreadyRendered = null;
  }
  currentBreakpoint = breakpoint;
  const stackCardDatum = getContentPackageStackCardDatum(data);
  const stylesLead = contentPackageLeadStyle(
    data.backgroundColor,
    data.stack[0]?.entity?.tint,
    data.stack[0]?.entity?.gradient
  );
  const stylesMedium = contentPackageMediumStyle(data.backgroundColor);
  const stylesSmall = contentPackageSmallStyle(data.backgroundColor);

  // In mobile, the second item visible. Otherwise, the first small item.
  const animatedImageKey =
    breakpoint.name === 'mobile' ? 1 : gridConfig.small[0];

  const hasHeaderSection = data.contentPackageHeader || data.description;

  return (
    <Container margin="0 auto" backgroundColor={data.backgroundColor}>
      {gridConfig.lead && stackCardDatum[0] && (
        <Container
          maxWidth="100rem"
          padding={
            hasHeaderSection
              ? ['0 0 32px 0', '0 0 0 0', '0 0 0 0', '56px 56px 0 56px']
              : ['0 0 32px 0', '0 0 24px 0', '0 0 40px 0', '56px']
          }
        >
          <GridRow gutter={[null, '12px', '20px', '28px']}>
            <GridColumn width={12}>
              <div css={styles.leadContainer}>
                {hasHeaderSection && (
                  <div css={styles.leadContainerHeader}>
                    <ContentPackageHeaderSection
                      title={data.contentPackageHeader}
                      description={data.description}
                    />
                  </div>
                )}

                <div css={styles.leadContainerCard}>
                  <CardItem
                    styles={stylesLead}
                    data={stackCardDatum[0]}
                    aspect="16x9"
                    isAboveTheFold={isAboveTheFold}
                  />
                </div>
              </div>
            </GridColumn>
          </GridRow>
        </Container>
      )}

      <Container
        maxWidth="100rem"
        padding={
          !gridConfig.lead && !hasHeaderSection
            ? ['20px', '24px', '40px', '56px']
            : [
                '0 20px 40px 20px',
                '0 24px 40px 24px',
                '0 40px 40px 40px',
                '0 56px 40px 56px'
              ]
        }
      >
        {!gridConfig.lead && hasHeaderSection && (
          <ContentPackageHeaderSection
            title={data.contentPackageHeader}
            description={data.description}
          />
        )}

        <GridRow gutter={[null, '12px', '20px', '28px']}>
          <GridColumn width={[12, 7, 8]} childrenMarginBottom="56px">
            {gridConfig.medium.map(key => (
              <CardItem
                styles={stylesMedium}
                data={stackCardDatum[key]}
                aspect="16x9"
                isAboveTheFold={isAboveTheFold}
                canBeAnimated={key === animatedImageKey}
                imageMaxWidths={{
                  mobile: 727,
                  tablet: 559,
                  small_desktop: 786,
                  large_desktop: 973
                }}
                key={`cardItem-medium-${key}`}
              />
            ))}
          </GridColumn>

          <GridColumn width={[12, 5, 4]} childrenMarginBottom="40px">
            {gridConfig.small.map(key => {
              return Number.isInteger(key) ? (
                <CardItem
                  styles={stylesSmall}
                  data={stackCardDatum[key]}
                  aspect="16x9"
                  isAboveTheFold={isAboveTheFold}
                  canBeAnimated={key === animatedImageKey}
                  imageMaxWidths={{
                    mobile: 727,
                    tablet: 392,
                    small_desktop: 373,
                    large_desktop: 459
                  }}
                  key={`cardItem-small-${key}`}
                />
              ) : (
                <Ad
                  key={`${key}__${data.id}`}
                  id={`${key}__${data.id}`}
                  content={content}
                />
              );
            })}
          </GridColumn>

          {gridConfig.bottom ? (
            <GridColumn width={12}>
              <Ad
                key={`${gridConfig.bottom}__${data.id}`}
                id={`${gridConfig.bottom}__${data.id}`}
                content={content}
              />
            </GridColumn>
          ) : null}
        </GridRow>
      </Container>
    </Container>
  );
};

ContentPackageGridTemplate.defaultProps = {
  isAboveTheFold: false
};

ContentPackageGridTemplate.propTypes = {
  gridConfig: PropTypes.shape({
    lead: PropTypes.bool.isRequired,
    medium: PropTypes.arrayOf(PropTypes.number).isRequired,
    small: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    ).isRequired,
    bottom: PropTypes.oneOfType([PropTypes.string, nullValidator])
  }).isRequired,
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  content: PropTypes.objectOf(PropTypes.any).isRequired,
  isAboveTheFold: PropTypes.bool
};
